/**
 * KnowledgeGraph — Dev mode → Knowledge → Graph (mockups/kiaan-knowledge.html, screen 1). Admin only.
 *
 *   Left:   containers ● › branches ◆ › files ▪, then each agent's chat memory ○
 *   Middle: the same tree as a radial graph — drag to pan, scroll to zoom, click to pick.
 *           Files from the last import get a green dashed ring; "Show only: last import" asks the
 *           server for just that import (`?since=`).
 *   Right:  the picked thing, and Remove. The server's preview (what goes, who stops seeing it) is
 *           shown BEFORE the confirm; a container also asks for its name.
 */
import './knowledge.css';
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent, type WheelEvent as RWheelEvent } from 'react';
import {
  createGroup, getKnowledgeGraph, knowledgeErrorText, knowledgeLoadErrorText, moveItem, previewRemove, removeKnowledge,
  type KGraph, type KImport, type KNode, type RemovePreview, type RemoveType,
} from './knowledgeApi';
import { childrenOf, colorOf, containerIndex, layoutGraph, MEMORY_COLOR } from './graphLayout';
import { whenText } from './whenText';

const NEW_GREEN = '#16A34A';
const BAD = '#B91C1C';

/** `g:<key>` → key, `f:<docId>` → docId, `m:<linkId>` → linkId. */
export const rawId = (id: string) => id.replace(/^[gfm]:/, '');

export interface KnowledgeGraphProps {
  /** Open filtered to this import (after an upload). */
  since?: string | null;
  onOpenMemory?: (agentId: string) => void;
  onUpload?: () => void;
}

export function KnowledgeGraph({ since: sinceProp = null, onOpenMemory, onUpload }: KnowledgeGraphProps) {
  const [since, setSince] = useState<string | null>(sinceProp);
  const [graph, setGraph] = useState<KGraph | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);

  useEffect(() => { setSince(sinceProp); }, [sinceProp]);

  useEffect(() => {
    let alive = true;
    getKnowledgeGraph(since)
      .then((g) => { if (alive) { setGraph(g); setError(null); } })
      .catch((e) => { if (alive) setError(knowledgeLoadErrorText(e)); });
    return () => { alive = false; };
  }, [since, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const nodes = useMemo(() => graph?.nodes ?? [], [graph]);
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const kids = useMemo(() => childrenOf(nodes), [nodes]);
  const cidx = useMemo(() => containerIndex(nodes), [nodes]);
  const lastId = graph?.lastImport?.id ?? null;
  const sel = selected ? byId.get(selected) ?? null : null;

  const pick = (id: string) => {
    setSelected(id);
    setNotice(null);
    // Open the path to it in the tree.
    setOpen((o) => {
      const next = new Set(o);
      for (let n = byId.get(id); n?.parentId; n = byId.get(n.parentId)) next.add(n.parentId);
      return next;
    });
  };

  const addContainer = async () => {
    const label = adding?.trim();
    if (!label) return;
    try {
      await createGroup(label, null);
      setAdding(null);
      setNotice(`Container “${label}” added. Only Admin can read it until you share it.`);
      reload();
    } catch (e) {
      setNotice(knowledgeErrorText(e));
    }
  };

  if (!graph) {
    return error ? <div className="kw-banner" role="status">{error}</div> : <div className="kw-small" style={{ padding: 12 }}>Loading…</div>;
  }

  const top = kids.get('root') ?? [];
  const containers = top.filter((n) => n.type === 'container');
  const memories = top.filter((n) => n.type === 'memory');
  const empty = containers.length === 0;

  return (
    <div className="kn-wrap" data-testid="knowledge-graph">
      <div className="kn-bar">
        {onUpload && <button type="button" className="kw-btn kw-light" onClick={onUpload}>⬆ Drop a folder</button>}
        {adding === null ? (
          <button type="button" className="kw-btn" onClick={() => setAdding('')}>＋ Container</button>
        ) : (
          <span className="kn-inline">
            <input className="kw-fld" aria-label="New container name" placeholder="Container name" value={adding} onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void addContainer(); }} autoFocus />
            <button type="button" className="kw-btn" onClick={() => void addContainer()} disabled={!adding.trim()}>Add</button>
            <button type="button" className="kw-btn kw-light" onClick={() => setAdding(null)}>Cancel</button>
          </span>
        )}
      </div>
      {error && <div className="kw-banner" role="status">{error}</div>}
      {notice && <div className="kw-banner kw-ok" role="status">{notice}</div>}
      <div className="kn-grid3">
        <aside className="kn-pane kn-l" aria-label="Knowledge tree">
          <div className="kn-h4">Containers</div>
          {empty && <div className="kw-small">No knowledge yet. Drop a folder to start.</div>}
          <div className="kn-tree">
            {containers.map((c) => (
              <TreeNode key={c.id} node={c} kids={kids} depth={0} open={open} lastId={lastId} selected={selected}
                color={colorOf(c, cidx.get(c.id))}
                onPick={pick} onToggle={(id) => setOpen((o) => { const n = new Set(o); if (n.has(id)) n.delete(id); else n.add(id); return n; })} />
            ))}
          </div>
          <div className="kn-h4">Chat memories</div>
          <div className="kn-tree">
            {memories.length === 0 && <div className="kw-small">No agent chats yet.</div>}
            {memories.map((m) => (
              <button key={m.id} type="button" className={`kn-ti${selected === m.id ? ' kn-sel' : ''}`} onClick={() => pick(m.id)}>
                <span className="kn-sq" style={{ background: MEMORY_COLOR, borderRadius: '50%' }} /><b>{m.label}</b><span className="kn-cnt">{m.chats ?? 0} chats</span>
              </button>
            ))}
          </div>
        </aside>

        <section className="kn-pane kn-mid" aria-label="Graph">
          <div className="kn-legend-row">
            <span className="kn-legend"><span>● container</span><span>◆ branch</span><span>▪ file</span><span style={{ color: MEMORY_COLOR }}>○ chat memory</span><span style={{ color: NEW_GREEN }}>◌ just imported</span></span>
            {graph.lastImport && <ImportPicker last={graph.lastImport} imports={graph.imports} since={since} onSince={(s) => { setSince(s); setSelected(null); }} />}
          </div>
          <GraphSvg nodes={nodes} kids={kids} lastId={lastId} selected={selected} onPick={pick} />
          <div className="kn-hint">
            Click any dot to see it on the right · drag to move · scroll to zoom ·{' '}
            <label><input type="checkbox" checked={!!since} disabled={!graph.lastImport} onChange={(e) => { setSince(e.target.checked ? graph.lastImport?.id ?? null : null); setSelected(null); }} /> <b>Show only: last import</b></label>
          </div>
        </section>

        <aside className="kn-pane kn-r" aria-label="Selected">
          {sel ? (
            <Inspector key={sel.id} node={sel} graph={graph} byId={byId} kids={kids}
              onDone={(line) => { setSelected(null); setNotice(line); reload(); }}
              onPick={pick} onOpenMemory={onOpenMemory} />
          ) : (
            <>
              <div className="kn-h4">Nothing picked</div>
              <div className="kw-small">Click a container, a branch, a file or a chat memory. You can remove exactly that thing.</div>
            </>
          )}
          <div className="kn-h4" style={{ marginTop: 8 }}>Other things you can point at</div>
          <div className="kw-small" style={{ lineHeight: 1.5 }}>A <b>branch</b> → remove the sub-folder and all its files. A <b>container</b> → asks you to type its name first. A <b>chat memory</b> → the Chat memory tab.</div>
        </aside>
      </div>
    </div>
  );
}

function ImportPicker({ last, imports, since, onSince }: { last: KImport; imports: KImport[]; since: string | null; onSince: (s: string | null) => void }) {
  const list = imports.length ? imports : [last];
  return (
    <select className="kw-fld kn-imp" aria-label="Pick an import" value={since ?? ''} onChange={(e) => onSince(e.target.value || null)}>
      <option value="">Last import: {last.files} {last.files === 1 ? 'file' : 'files'} · {whenText(last.at)}</option>
      {list.map((i) => <option key={i.id} value={i.id}>Only {whenText(i.at)} · {i.files} files · {i.source === 'desktop' ? 'Desktop sync' : 'browser'}</option>)}
    </select>
  );
}

function TreeNode({ node, kids, depth, open, lastId, selected, color, onPick, onToggle }: {
  node: KNode; kids: Map<string | null, KNode[]>; depth: number; open: Set<string>; lastId: string | null; selected: string | null; color: string;
  onPick: (id: string) => void; onToggle: (id: string) => void;
}) {
  const children = kids.get(node.id) ?? [];
  const isOpen = node.type === 'container' || open.has(node.id);
  const pad = 6 + depth * 14;
  if (node.type === 'file') {
    return (
      <button type="button" className={`kn-ti kn-f${selected === node.id ? ' kn-sel' : ''}`} style={{ paddingLeft: pad }} onClick={() => onPick(node.id)}>
        <span className="kn-lbl">{node.label}</span>{lastId && node.importId === lastId && <span className="kn-new">new</span>}
      </button>
    );
  }
  return (
    <>
      <div className={`kn-ti${selected === node.id ? ' kn-sel' : ''}`} style={{ paddingLeft: pad }}>
        {node.type === 'container' ? (
          <span className="kn-sq" style={{ background: color }} />
        ) : (
          <button type="button" className="kn-tog" aria-label={`${isOpen ? 'Close' : 'Open'} ${node.label}`} onClick={() => onToggle(node.id)}>{isOpen ? '▾' : '▸'}</button>
        )}
        <button type="button" className="kn-lblbtn" onClick={() => onPick(node.id)}>{node.type === 'container' ? <b>{node.label}</b> : node.label}</button>
        <span className="kn-cnt">{node.files ?? children.filter((c) => c.type === 'file').length}</span>
      </div>
      {isOpen && children.map((c) => (
        <TreeNode key={c.id} node={c} kids={kids} depth={depth + 1} open={open} lastId={lastId} selected={selected} color={color} onPick={onPick} onToggle={onToggle} />
      ))}
    </>
  );
}

function GraphSvg({ nodes, kids, lastId, selected, onPick }: { nodes: KNode[]; kids: Map<string | null, KNode[]>; lastId: string | null; selected: string | null; onPick: (id: string) => void }) {
  const pos = useMemo(() => layoutGraph(nodes), [nodes]);
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const drag = useRef<{ px: number; py: number; x: number; y: number } | null>(null);
  const fileCount = nodes.filter((n) => n.type === 'file').length;

  const down = (e: RPointerEvent<SVGSVGElement>) => { drag.current = { px: e.clientX, py: e.clientY, x: view.x, y: view.y }; };
  const move = (e: RPointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (d) setView((v) => ({ ...v, x: d.x + (e.clientX - d.px), y: d.y + (e.clientY - d.py) }));
  };
  const up = () => { drag.current = null; };
  const wheel = (e: RWheelEvent<SVGSVGElement>) => {
    const k = Math.min(3, Math.max(0.4, view.k * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
    setView((v) => ({ ...v, k }));
  };

  return (
    <svg className="kn-svg" viewBox="0 0 620 470" role="img" aria-label="Knowledge graph" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} onWheel={wheel}>
      <g transform={`translate(${view.x} ${view.y}) translate(310 235) scale(${view.k}) translate(-310 -235)`}>
        {nodes.map((n) => {
          if (n.type === 'root') return null;
          const p = pos.get(n.id);
          const parent = n.parentId && pos.get(n.parentId) ? pos.get(n.parentId) : pos.get('root');
          if (!p || !parent) return null;
          const mem = n.type === 'memory';
          return <line key={`e-${n.id}`} x1={parent.x} y1={parent.y} x2={p.x} y2={p.y} stroke={mem ? '#99D5CB' : '#D6CCB8'} strokeWidth={mem ? 1.2 : 1.4} strokeDasharray={mem ? '4 3' : undefined} />;
        })}
        {nodes.map((n) => {
          const p = pos.get(n.id);
          if (!p) return null;
          const fresh = n.type === 'file' && !!lastId && n.importId === lastId;
          const isSel = selected === n.id;
          const showLabel = n.type !== 'file' || isSel || fileCount <= 12;
          return (
            <g key={n.id} data-testid={`node-${n.id}`} data-fresh={fresh ? '1' : undefined} role="button" aria-label={`${n.type} ${n.label}`} tabIndex={0} style={{ cursor: 'pointer' }}
              onPointerDown={(e) => e.stopPropagation()} onClick={() => onPick(n.id)} onKeyDown={(e) => { if (e.key === 'Enter') onPick(n.id); }}>
              <title>{n.label}</title>
              <Shape n={n} x={p.x} y={p.y} color={p.color} fresh={fresh} kidsCount={(kids.get(n.id) ?? []).length} />
              {isSel && <circle cx={p.x} cy={p.y} r={n.type === 'root' ? 28 : n.type === 'container' ? 22 : 12} fill="none" stroke={BAD} strokeWidth={2.5} />}
              {showLabel && n.type !== 'root' && (
                <text x={p.x} y={p.y + (n.type === 'container' ? -22 : n.type === 'memory' ? -13 : 20)} textAnchor="middle" fontSize={n.type === 'container' ? 11 : 10} fill={n.type === 'memory' ? MEMORY_COLOR : undefined}>
                  {n.type === 'memory' ? `${n.label} · ${n.chats ?? 0}` : n.label.length > 26 ? `${n.label.slice(0, 25)}…` : n.label}
                </text>
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

function Shape({ n, x, y, color, fresh, kidsCount }: { n: KNode; x: number; y: number; color: string; fresh: boolean; kidsCount: number }) {
  if (n.type === 'root') {
    return (
      <>
        <circle cx={x} cy={y} r={22} fill="#FFF7E8" stroke="#E2A35B" strokeWidth={2} />
        <text x={x} y={y + 4} textAnchor="middle" fontSize={11} fontWeight={700}>{n.label.length > 12 ? `${n.label.slice(0, 11)}…` : n.label}</text>
      </>
    );
  }
  if (n.type === 'container') return <circle cx={x} cy={y} r={Math.min(18, 13 + kidsCount)} fill={color} />;
  if (n.type === 'branch') return <rect x={x - 7} y={y - 7} width={14} height={14} transform={`rotate(45 ${x} ${y})`} fill={color} />;
  if (n.type === 'memory') return <circle cx={x} cy={y} r={8} fill="#fff" stroke={MEMORY_COLOR} strokeWidth={2} />;
  return <rect x={x - 5} y={y - 5} width={10} height={10} fill={color} stroke={fresh ? NEW_GREEN : undefined} strokeWidth={fresh ? 2 : undefined} strokeDasharray={fresh ? '3 2' : undefined} />;
}

// ── Inspector ─────────────────────────────────────────────────────────────

function pathOf(n: KNode, byId: Map<string, KNode>): string {
  const parts: string[] = [];
  for (let p = n.parentId ? byId.get(n.parentId) : undefined; p && p.type !== 'root'; p = p.parentId ? byId.get(p.parentId) : undefined) parts.unshift(p.label);
  return parts.join(' › ') || '—';
}

const NOUN: Record<RemoveType, string> = { file: 'file', branch: 'branch', container: 'container' };

/** The preview lines, in plain words (what goes, who stops seeing it). Exported for the tests. */
export function previewLines(p: RemovePreview): string[] {
  const out: string[] = [];
  if (p.type === 'file') out.push(`${p.pieces} ${p.pieces === 1 ? 'piece is' : 'pieces are'} deleted from search`);
  else out.push(`${p.files} ${p.files === 1 ? 'file' : 'files'}${p.branches ? ` in ${p.branches} ${p.branches === 1 ? 'branch' : 'branches'}` : ''} and ${p.pieces} pieces are deleted from search`);
  if (p.agents.length) out.push(`${joinNames(p.agents.map((a) => a.name))} stop seeing it`);
  else out.push('No agent reads it now');
  if (p.roles.length) out.push(`Roles that lose it: ${p.roles.join(', ')}`);
  if (p.people > 0) out.push(`${p.people} ${p.people === 1 ? 'person' : 'people'} with special access lose it`);
  out.push('Nothing else changes.');
  if (p.willSkipOnSync > 0) out.push(`Desktop sync will not bring ${p.willSkipOnSync === 1 ? 'it' : `these ${p.willSkipOnSync} files`} back (added to "skip").`);
  return out;
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function Inspector({ node, graph, byId, kids, onDone, onPick, onOpenMemory }: {
  node: KNode; graph: KGraph; byId: Map<string, KNode>; kids: Map<string | null, KNode[]>;
  onDone: (line: string) => void; onPick: (id: string) => void; onOpenMemory?: (agentId: string) => void;
}) {
  const removable = node.type === 'file' || node.type === 'branch' || node.type === 'container';
  const type = (removable ? node.type : 'file') as RemoveType;
  const [preview, setPreview] = useState<RemovePreview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState(false);
  const [branchName, setBranchName] = useState<string | null>(null);

  useEffect(() => {
    if (!removable) return;
    let alive = true;
    previewRemove(type, rawId(node.id))
      .then((p) => { if (alive) setPreview(p); })
      .catch((e) => { if (alive) setErr(knowledgeErrorText(e)); });
    return () => { alive = false; };
  }, [node.id, removable, type]);

  const doRemove = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await removeKnowledge(type, rawId(node.id), type === 'container' ? typed : undefined);
      onDone(r.line || `Removed ${node.label}.`);
    } catch (e) {
      setErr(knowledgeErrorText(e));
      setBusy(false);
    }
  };

  const doMove = async (to: string) => {
    setBusy(true);
    setErr(null);
    try {
      await moveItem(node.type === 'file' ? 'file' : 'branch', rawId(node.id), to === '__top__' ? null : rawId(to));
      onDone(`Moved ${node.label}.`);
    } catch (e) {
      setErr(knowledgeErrorText(e));
      setBusy(false);
    }
  };

  const addBranch = async () => {
    const label = branchName?.trim();
    if (!label) return;
    setBusy(true);
    try {
      await createGroup(label, rawId(node.id));
      onDone(`Branch “${label}” added under ${node.label}.`);
    } catch (e) {
      setErr(knowledgeErrorText(e));
      setBusy(false);
    }
  };

  if (node.type === 'memory') {
    return (
      <>
        <div className="kn-h4">Selected · chat memory</div>
        <b className="kn-title">{node.label}</b>
        <div className="kn-kv"><span>Chats</span><span>{node.chats ?? 0}</span><span>Stored as</span><span>Kiaan Work chats + {node.label}'s session for each</span></div>
        {onOpenMemory && node.agentId && <button type="button" className="kw-btn kw-danger-btn" onClick={() => onOpenMemory(node.agentId!)}>Choose what to forget…</button>}
      </>
    );
  }
  if (node.type === 'root') {
    const all = graph.nodes;
    return (
      <>
        <div className="kn-h4">Selected · everything</div>
        <b className="kn-title">{node.label}</b>
        <div className="kn-kv">
          <span>Containers</span><span>{all.filter((n) => n.type === 'container').length}</span>
          <span>Files</span><span>{all.filter((n) => n.type === 'file').length}</span>
          <span>Pieces</span><span>{all.reduce((s, n) => s + (n.pieces ?? 0), 0)}</span>
        </div>
      </>
    );
  }

  const imp = node.importId ? graph.imports.find((i) => i.id === node.importId) : undefined;
  const source = node.source === 'desktop' ? 'Desktop sync' : node.source === 'web' ? 'Dropped in the browser' : '—';
  const moveTargets = graph.nodes.filter((n) => {
    if (node.type === 'file') return (n.type === 'container' || n.type === 'branch') && n.id !== node.parentId;
    // A branch can go under a container (not itself, not its own parent) or become a container.
    return n.type === 'container' && n.id !== node.parentId && n.id !== node.id;
  });
  const newer = node.newer;

  return (
    <>
      <div className="kn-h4">Selected · {NOUN[type]}</div>
      <b className="kn-title">{node.label}</b>
      <div className="kn-kv">
        <span>Where</span><span>{node.type === 'container' ? 'Top level' : pathOf(node, byId)}</span>
        {node.type === 'file' && <><span>Came from</span><span>{source}{imp ? ` · ${whenText(imp.at)}` : ''}</span></>}
        {node.type === 'file' && node.kind && <><span>Kind</span><span>{node.kind === 'STATE' ? 'STATE (current truth)' : 'EVENT (dated record)'}</span></>}
        {node.type === 'file' ? <><span>Pieces</span><span>{node.pieces ?? 0} searchable pieces</span></> : <><span>Files</span><span>{node.files ?? 0}{node.type === 'container' ? ` in ${(kids.get(node.id) ?? []).filter((c) => c.type === 'branch').length} branches` : ''}</span></>}
        {preview && <><span>Who can read</span><span>{preview.roles.length ? preview.roles.join(', ') : 'Admin only'}</span></>}
        {preview && <><span>Agents using it</span><span>{preview.agents.length ? preview.agents.map((a) => a.name).join(', ') : 'none'}</span></>}
      </div>
      {newer && (
        <div className="kn-card" data-testid="kiaan-noticed">
          <b>Kiaan noticed</b>
          <span>A newer file covers the same thing: <button type="button" className="kn-link" onClick={() => onPick(newer.id)}>{newer.label}</button>{newer.at ? ` (imported ${whenText(newer.at)})` : ''}. Old facts can give wrong answers.</span>
        </div>
      )}
      {preview && (
        <div className="kn-warn" data-testid="remove-preview">
          <b>Remove exactly this {NOUN[type]}?</b>
          <ul>{previewLines(preview).map((l) => <li key={l}>{l}</li>)}</ul>
        </div>
      )}
      {err && <div className="kw-banner kw-danger" role="alert">{err}</div>}
      {!confirming ? (
        <div className="kn-row">
          <button type="button" className="kw-btn kn-red" disabled={!preview || busy} onClick={() => setConfirming(true)}>Remove this {NOUN[type]}</button>
          {node.type !== 'container' && <button type="button" className="kw-btn kw-light" disabled={busy} onClick={() => setMoving((m) => !m)}>Move…</button>}
          {node.type === 'container' && branchName === null && <button type="button" className="kw-btn kw-light" onClick={() => setBranchName('')}>＋ Branch</button>}
        </div>
      ) : (
        <div className="kn-confirm" role="dialog" aria-label={`Confirm removing ${node.label}`}>
          {type === 'container' ? (
            <label className="kw-small">Type <b>{node.label}</b> to confirm
              <input className="kw-fld" aria-label="Type the container name" value={typed} onChange={(e) => setTyped(e.target.value)} />
            </label>
          ) : (
            <span>This cannot be undone. The audit log keeps a line about it.</span>
          )}
          <div className="kn-row">
            <button type="button" className="kw-btn kn-red" disabled={busy || (type === 'container' && typed.trim() !== node.label)} onClick={() => void doRemove()}>{busy ? 'Removing…' : `Yes, remove ${NOUN[type]}`}</button>
            <button type="button" className="kw-btn kw-light" disabled={busy} onClick={() => { setConfirming(false); setTyped(''); }}>Cancel</button>
          </div>
        </div>
      )}
      {moving && (
        <select className="kw-fld" aria-label="Move to" defaultValue="" onChange={(e) => e.target.value && void doMove(e.target.value)} disabled={busy}>
          <option value="">Move to…</option>
          {node.type === 'branch' && <option value="__top__">Top level (make it a container)</option>}
          {moveTargets.map((t) => <option key={t.id} value={t.id}>{t.type === 'branch' ? `${pathOf(t, byId)} › ${t.label}` : t.label}</option>)}
        </select>
      )}
      {branchName !== null && (
        <span className="kn-inline">
          <input className="kw-fld" aria-label="New branch name" placeholder="Branch name" value={branchName} onChange={(e) => setBranchName(e.target.value)} />
          <button type="button" className="kw-btn" disabled={busy || !branchName.trim()} onClick={() => void addBranch()}>Add</button>
          <button type="button" className="kw-btn kw-light" onClick={() => setBranchName(null)}>Cancel</button>
        </span>
      )}
    </>
  );
}
